import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketing, owner as ownerApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Sparkles, Loader2, Megaphone, Mail, MessageCircle, Trash2, Eye, Play, Pause, Send } from 'lucide-react';
import { MarketingLocked } from './LandingPageBuilder';

// Wave B — Campaign Scheduler. AI drafts subject + email + SMS from the
// business profile. Owner reviews, one-taps "Approve", scheduler dispatches.
export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [locked, setLocked] = useState(false);
  const [genBid, setGenBid] = useState('');
  const [focus, setFocus] = useState('');
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [cs, bs] = await Promise.all([
        marketing.listCampaigns(),
        ownerApi.myBusinesses().catch(() => []),
      ]);
      setCampaigns(cs || []);
      setBusinesses(bs || []);
      if (!genBid && bs?.length) setGenBid(bs[0].id);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else toast({ title: 'Failed to load campaigns' });
      setCampaigns([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generate = async () => {
    if (!genBid) { toast({ title: 'Pick a business first' }); return; }
    setGenerating(true);
    try {
      const c = await marketing.autoGenerateCampaign(genBid, focus);
      setCampaigns(prev => [c, ...(prev || [])]);
      toast({ title: 'AI draft ready', description: 'Review, edit, and approve to schedule.' });
      setFocus('');
    } catch (e) {
      toast({ title: 'Generate failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setGenerating(false); }
  };

  const doApprove = async (c) => {
    try {
      const r = await marketing.approveCampaign(c.id);
      setCampaigns(prev => prev.map(x => x.id === c.id ? r : x));
      toast({ title: 'Approved — will send at scheduled time' });
    } catch (e) { toast({ title: 'Approve failed', description: e?.response?.data?.detail || '' }); }
  };
  const doPause = async (c) => {
    try {
      await marketing.pauseCampaign(c.id);
      setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, status: 'paused' } : x));
      toast({ title: 'Paused' });
    } catch (e) { toast({ title: 'Pause failed', description: e?.response?.data?.detail || '' }); }
  };
  const doSendNow = async (c) => {
    if (!window.confirm('Send this campaign now to everyone in the segment?')) return;
    try {
      const r = await marketing.sendCampaignNow(c.id);
      toast({ title: 'Dispatched', description: `queued=${r.queued} email=${r.emailSent} sms=${r.smsSent}` });
      load();
    } catch (e) { toast({ title: 'Send failed', description: e?.response?.data?.detail || '' }); }
  };
  const doDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.subject}"?`)) return;
    try {
      await marketing.deleteCampaign(c.id);
      setCampaigns(prev => prev.filter(x => x.id !== c.id));
      toast({ title: 'Deleted' });
    } catch (e) { toast({ title: 'Delete failed', description: e?.response?.data?.detail || '' }); }
  };

  if (locked) return <MarketingLocked />;
  if (campaigns === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  return (
    <div className="space-y-4" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Megaphone size={22}/> Campaigns</h1>
          <p className="text-sm text-gray-400 mt-1">AI drafts email + SMS from your business profile. Review and one-tap approve to send.</p>
        </div>
      </div>

      {/* Generate new draft */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
        <div className="text-white font-semibold flex items-center gap-2"><Sparkles size={16} className="text-green-400"/> Generate a new campaign</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
          <select data-testid="campaign-gen-business"
                  value={genBid} onChange={e => setGenBid(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm">
            {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            {!businesses.length && <option value="">No businesses</option>}
          </select>
          <input data-testid="campaign-gen-focus"
                 value={focus} onChange={e => setFocus(e.target.value)}
                 placeholder="Optional focus (e.g. 'spring sale', 'new arrivals')"
                 className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm placeholder-gray-500"/>
          <button data-testid="campaign-generate-btn"
                  onClick={generate} disabled={generating || !genBid}
                  className="pbb-btn-green inline-flex items-center justify-center gap-1 disabled:opacity-60">
            {generating ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
            Generate draft
          </button>
        </div>
      </div>

      {/* List */}
      {campaigns.length === 0 ? (
        <div className="text-gray-500 text-sm border border-dashed border-gray-800 rounded-lg p-6 text-center">
          No campaigns yet. Generate your first AI draft above — you can edit before it sends.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex flex-col gap-2"
                 data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-white font-semibold truncate">{c.subject || '(no subject)'}</div>
                  <div className="text-xs text-gray-400 truncate">{c.smsBody?.slice(0, 90) || '—'}</div>
                </div>
                <StatusPill status={c.status}/>
              </div>

              <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                <span className="inline-flex items-center gap-1"><Mail size={12}/> {c.stats?.emailSent || 0} sent · {c.stats?.emailDelivered || 0} delivered · {c.stats?.emailOpened || 0} opened</span>
                <span className="inline-flex items-center gap-1"><MessageCircle size={12}/> {c.stats?.smsSent || 0} sent · {c.stats?.smsDelivered || 0} delivered</span>
              </div>
              <div className="text-xs text-gray-500">
                Send at: <span className="text-gray-300">{c.sendAt ? new Date(c.sendAt).toLocaleString() : '—'}</span>
                {' · '}Segment: <span className="text-gray-300">{c.segment?.type || 'all'}{c.segment?.value ? `:${c.segment.value}` : ''}</span>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Link to={`/marketing/campaigns/${c.id}`} data-testid={`campaign-view-${c.id}`}
                      className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
                  <Eye size={12}/> Review & edit
                </Link>
                {c.status === 'draft' && (
                  <button data-testid={`campaign-approve-${c.id}`} onClick={() => doApprove(c)}
                          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded bg-green-600 hover:bg-green-500 text-white">
                    <Play size={12}/> Approve to schedule
                  </button>
                )}
                {c.status === 'scheduled' && (
                  <button data-testid={`campaign-pause-${c.id}`} onClick={() => doPause(c)}
                          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
                    <Pause size={12}/> Pause
                  </button>
                )}
                {c.status === 'paused' && (
                  <button data-testid={`campaign-approve-${c.id}`} onClick={() => doApprove(c)}
                          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded bg-green-600 hover:bg-green-500 text-white">
                    <Play size={12}/> Resume
                  </button>
                )}
                {(c.status === 'draft' || c.status === 'scheduled' || c.status === 'paused') && (
                  <button data-testid={`campaign-send-now-${c.id}`} onClick={() => doSendNow(c)}
                          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
                    <Send size={12}/> Send now
                  </button>
                )}
                {c.status !== 'sending' && (
                  <button data-testid={`campaign-delete-${c.id}`} onClick={() => doDelete(c)}
                          className="ml-auto text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-red-800 text-red-400 hover:bg-red-950">
                    <Trash2 size={12}/> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: ['bg-yellow-900/40 text-yellow-300 border-yellow-800', 'Draft'],
    scheduled: ['bg-blue-900/40 text-blue-300 border-blue-800', 'Scheduled'],
    sending: ['bg-purple-900/40 text-purple-300 border-purple-800', 'Sending…'],
    sent: ['bg-green-900/40 text-green-300 border-green-800', 'Sent'],
    paused: ['bg-gray-800 text-gray-300 border-gray-700', 'Paused'],
    cancelled: ['bg-red-900/40 text-red-300 border-red-800', 'Cancelled'],
  };
  const [cls, label] = map[status] || map.draft;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls} whitespace-nowrap`}>{label}</span>;
}
